import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { extractDesignQuestions } from "@/lib/form-conditions";
import { useSaveProcessTrigger } from "@/hooks/process-trigger/use-process-trigger";
import type {
  ProcessTrigger,
  TriggerApplicantSource,
  UpsertProcessTriggerInput,
} from "@/types/process-trigger.types";
import ConditionGroupBuilder, { TriggerStep } from "./condition-group-builder";
import SearchableSelect, { SearchableOption } from "./searchable-select";

const USERS_QUESTION_TYPE = "Add Users";
const APPLICANT_SOURCES: TriggerApplicantSource[] = [
  "PARENT_APPLICANT",
  "FROM_ANSWER",
  "COMPLETING_REVIEWER",
  "FIXED_USER",
];

interface Props {
  open: boolean;
  onClose: () => void;
  processId: string;
  steps: TriggerStep[];
  trigger?: ProcessTrigger | null;
  processOptions: SearchableOption[];
  userOptions: SearchableOption[];
}

const emptyInput = (): UpsertProcessTriggerInput => ({
  name: "",
  event: "PROCESS_COMPLETED",
  sourceFormId: null,
  targetProcessId: "",
  mode: "STATIC",
  conditions: null,
  applicantSource: "PARENT_APPLICANT",
  applicantFormId: null,
  applicantQuestionId: null,
  fixedUserId: null,
  message: null,
  allowDecline: false,
  dueInDays: null,
  reminderEveryDays: null,
  isActive: true,
});

const toInput = (trigger: ProcessTrigger): UpsertProcessTriggerInput => {
  const { id: _id, processId: _p, targetProcess: _t, fixedUser: _f, ...input } = trigger;
  return input;
};

const optionalNumber = (value: string) => (value.trim() === "" ? null : Number(value));

const TriggerEditorDialog: React.FC<Props> = ({
  open,
  onClose,
  processId,
  steps,
  trigger,
  processOptions,
  userOptions,
}) => {
  const { t } = useTranslation();
  const [input, setInput] = useState<UpsertProcessTriggerInput>(emptyInput);
  const saveMutation = useSaveProcessTrigger(processId);

  useEffect(() => {
    if (open) setInput(trigger ? toInput(trigger) : emptyInput());
  }, [open, trigger]);

  const set = <K extends keyof UpsertProcessTriggerInput>(
    key: K,
    value: UpsertProcessTriggerInput[K]
  ) => setInput((prev) => ({ ...prev, [key]: value }));

  // A step trigger can only use answers that exist when that step completes.
  const sourceStep = steps.find((s) => s.formId === input.sourceFormId);
  const readableSteps = useMemo(
    () =>
      input.event === "STEP_COMPLETED"
        ? sourceStep
          ? steps.filter((s) => s.order <= sourceStep.order)
          : []
        : steps,
    [input.event, sourceStep, steps]
  );

  const usersQuestions = useMemo(
    () =>
      readableSteps.flatMap((step) =>
        extractDesignQuestions(step.design, (q) => q.type === USERS_QUESTION_TYPE).map((q) => ({
          value: `${step.formId}::${q.id}`,
          label: `${step.order}. ${step.name} → ${q.label}`,
        }))
      ),
    [readableSteps]
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload: UpsertProcessTriggerInput = {
      ...input,
      sourceFormId: input.event === "STEP_COMPLETED" ? input.sourceFormId : null,
      conditions: input.mode === "RULES" ? input.conditions : null,
      message: input.message?.trim() || null,
    };
    try {
      await saveMutation.mutateAsync({ id: trigger?.id, input: payload });
      toast.success(t("processTriggers.editor.saved"));
      onClose();
    } catch (error) {
      const message = axios.isAxiosError(error) ? error.response?.data?.message : undefined;
      toast.error(
        Array.isArray(message) ? message.join(", ") : message || t("processTriggers.editor.saveFailed")
      );
    }
  };

  const isSelfTarget = input.targetProcessId === processId;

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {trigger ? t("processTriggers.editor.editTitle") : t("processTriggers.editor.addTitle")}
          </DialogTitle>
          <DialogDescription>{t("processTriggers.editor.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1">
            <Label htmlFor="trigger-name">{t("processTriggers.editor.name")}</Label>
            <Input
              id="trigger-name"
              value={input.name}
              maxLength={120}
              required
              placeholder={t("processTriggers.editor.namePlaceholder")}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>{t("processTriggers.editor.when")}</Label>
              <Select
                value={input.event}
                onValueChange={(value) => set("event", value as UpsertProcessTriggerInput["event"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PROCESS_COMPLETED">
                    {t("processTriggers.event.PROCESS_COMPLETED")}
                  </SelectItem>
                  <SelectItem value="STEP_COMPLETED">
                    {t("processTriggers.event.STEP_COMPLETED")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {input.event === "STEP_COMPLETED" && (
              <div className="space-y-1">
                <Label>{t("processTriggers.editor.step")}</Label>
                <Select
                  value={input.sourceFormId ?? ""}
                  onValueChange={(value) => set("sourceFormId", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("processTriggers.editor.chooseStep")} />
                  </SelectTrigger>
                  <SelectContent>
                    {steps.map((step) => (
                      <SelectItem key={step.formId} value={step.formId}>
                        {step.order}. {step.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Label>{t("processTriggers.editor.target")}</Label>
            <SearchableSelect
              options={processOptions}
              value={input.targetProcessId}
              onChange={(value) => set("targetProcessId", value)}
              placeholder={t("processTriggers.editor.chooseProcess")}
              searchPlaceholder={t("processTriggers.editor.searchProcess")}
              emptyText={t("processTriggers.editor.noProcess")}
            />
            {isSelfTarget && (
              <p className="flex items-center gap-1 text-xs text-amber-700">
                <AlertTriangle className="h-3 w-3" /> {t("processTriggers.editor.selfTarget")}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t("processTriggers.editor.mode")}</Label>
            <Select
              value={input.mode}
              onValueChange={(value) => {
                const mode = value as UpsertProcessTriggerInput["mode"];
                setInput((prev) => ({
                  ...prev,
                  mode,
                  conditions:
                    mode === "RULES" ? prev.conditions ?? { match: "ALL", conditions: [] } : null,
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="STATIC">{t("processTriggers.mode.STATIC")}</SelectItem>
                <SelectItem value="RULES">{t("processTriggers.mode.RULES")}</SelectItem>
              </SelectContent>
            </Select>

            {input.mode === "RULES" && (
              <div className="rounded-lg border border-gray-200 p-3">
                {readableSteps.length === 0 ? (
                  <p className="text-sm text-gray-500">{t("processTriggers.editor.chooseStepFirst")}</p>
                ) : (
                  <ConditionGroupBuilder
                    steps={readableSteps}
                    value={input.conditions ?? { match: "ALL", conditions: [] }}
                    onChange={(conditions) => set("conditions", conditions)}
                  />
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t("processTriggers.editor.recipient")}</Label>
            <Select
              value={input.applicantSource}
              onValueChange={(value) =>
                setInput((prev) => ({
                  ...prev,
                  applicantSource: value as TriggerApplicantSource,
                  applicantFormId: null,
                  applicantQuestionId: null,
                  fixedUserId: null,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {APPLICANT_SOURCES.map((source) => (
                  <SelectItem key={source} value={source}>
                    {t(`processTriggers.applicantSource.${source}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {input.applicantSource === "FROM_ANSWER" &&
              (usersQuestions.length === 0 ? (
                <p className="text-sm text-gray-500">{t("processTriggers.editor.noUsersQuestion")}</p>
              ) : (
                <Select
                  value={
                    input.applicantFormId && input.applicantQuestionId
                      ? `${input.applicantFormId}::${input.applicantQuestionId}`
                      : ""
                  }
                  onValueChange={(value) => {
                    const [applicantFormId, applicantQuestionId] = value.split("::");
                    setInput((prev) => ({ ...prev, applicantFormId, applicantQuestionId }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("processTriggers.editor.chooseUsersQuestion")} />
                  </SelectTrigger>
                  <SelectContent>
                    {usersQuestions.map((q) => (
                      <SelectItem key={q.value} value={q.value}>
                        {q.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ))}

            {input.applicantSource === "FIXED_USER" && (
              <SearchableSelect
                options={userOptions}
                value={input.fixedUserId}
                onChange={(value) => set("fixedUserId", value)}
                placeholder={t("processTriggers.editor.chooseUser")}
                searchPlaceholder={t("processTriggers.editor.searchUser")}
                emptyText={t("processTriggers.editor.noUser")}
              />
            )}

            {input.applicantSource !== "PARENT_APPLICANT" && (
              <p className="flex items-start gap-1 text-xs text-amber-700">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                {t("processTriggers.editor.privacyNote")}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="trigger-message">{t("processTriggers.editor.message")}</Label>
            <Textarea
              id="trigger-message"
              rows={3}
              maxLength={2000}
              value={input.message ?? ""}
              placeholder={t("processTriggers.editor.messagePlaceholder")}
              onChange={(e) => set("message", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="trigger-due">{t("processTriggers.editor.dueInDays")}</Label>
              <Input
                id="trigger-due"
                type="number"
                min={1}
                max={365}
                value={input.dueInDays ?? ""}
                placeholder={t("processTriggers.editor.noDeadline")}
                onChange={(e) => set("dueInDays", optionalNumber(e.target.value))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="trigger-reminder">{t("processTriggers.editor.reminderEveryDays")}</Label>
              <Input
                id="trigger-reminder"
                type="number"
                min={1}
                max={90}
                value={input.reminderEveryDays ?? ""}
                placeholder={t("processTriggers.editor.noReminders")}
                onChange={(e) => set("reminderEveryDays", optionalNumber(e.target.value))}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={input.allowDecline}
                onCheckedChange={(checked) => set("allowDecline", checked)}
              />
              {t("processTriggers.editor.allowDecline")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={input.isActive}
                onCheckedChange={(checked) => set("isActive", checked)}
              />
              {t("processTriggers.editor.active")}
            </label>
          </div>

          <DialogFooter className="gap-2">
            <button type="button" className="px-4 py-2 text-sm border rounded" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="main-dark-button" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? t("common.saving") : t("common.save")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TriggerEditorDialog;
